import { AsyncPipe, DatePipe } from '@angular/common';
import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ButtonModule } from 'primeng/button';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Table, TableModule } from 'primeng/table';
import { debounceTime, Subject, BehaviorSubject, combineLatest, map, startWith } from 'rxjs';
import { Restriction } from 'src/app/models/restriction';
import { CardModule } from 'primeng/card';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { FormsModule } from '@angular/forms';
import { RestrictionsService } from 'src/app/services/restrictions.service';
import { UserService } from 'src/app/services/user.service';
import { RestrictionDialogComponent } from "../restriction-dialog/restriction-dialog.component";
import { Dialog } from "primeng/dialog";
import { SortEvent } from 'primeng/api';
import { MOBILE_BREAKPOINT } from 'src/app/shared/breakpoints';

@Component({
  selector: 'app-restrictions-table',
  standalone: true,
  imports: [
    AsyncPipe,
    ButtonModule,
    CardModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    AsyncPipe,
    RestrictionDialogComponent,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    DatePipe,
    Dialog
  ],
  templateUrl: './restrictions-table.component.html',
  styleUrl: './restrictions-table.component.scss'
})
export class RestrictionsTableComponent implements OnInit {
  @Input() archived = false;

  first: number = 0;
  isAdmin$ = this.userService.isAdmin$;
  restrictions$ = this.restrictionsService.restrictions$;
  selectedRestriction: Restriction | null = null;
  filterTextChanged = new Subject<string>();
  showDialog = false;
  @ViewChild('restrictionTable') restrictionTable!: Table;
  restrictionToDelete: Restriction | null = null;
  showDeleteConfirmDialog = false;
  isMobile$ = this.breakpointObserver.observe(MOBILE_BREAKPOINT).pipe(map(r => r.matches));

  public searchTermControl = new FormControl<string>('')
  public searchTerm = ""
  public sortOrderSubject = new BehaviorSubject<boolean>(true)
  public filteredAndSortedRestrictions$ = this.restrictionsService.restrictions$.pipe(map(r => r));

  constructor(
    private restrictionsService: RestrictionsService,
    private userService: UserService,
    private breakpointObserver: BreakpointObserver
  ) {}

  ngOnInit(): void {
    if (!this.archived) {
      this.restrictionsService.clearCurrentVehicleName();
    }

    const source$ = this.archived
      ? this.restrictionsService.getArchivedRestrictions$()
      : this.restrictionsService.restrictions$;

    this.restrictions$ = source$;

    this.filteredAndSortedRestrictions$ = combineLatest({
      restrictions: source$,
      searchTerm: this.searchTermControl.valueChanges.pipe(startWith('')),
      sortOrder: this.sortOrderSubject.asObservable()
    }).pipe(
      map(source => {
        this.searchTerm = source.searchTerm!;

        let restrictions = source.restrictions ?? [];

        if (restrictions.length && source.searchTerm) {
          const searchLower = source.searchTerm.toLowerCase();
          restrictions = restrictions.filter(restriction => {
            const normalize = (str: string) => str.toLowerCase().replace(/\s+/g, '');
            const searchNormalized = normalize(searchLower);
            return normalize(restriction.displayName).includes(searchNormalized) ||
                   normalize(restriction.restrictionName).includes(searchNormalized) ||
                   normalize(restriction.responsible).includes(searchNormalized);
          });
        }

        if (restrictions.length) {
          restrictions.sort((a, b) => {
            try {
              const nameA = a && typeof a.restrictionName === 'string' ? a.restrictionName.toLowerCase() : '';
              const nameB = b && typeof b.restrictionName === 'string' ? b.restrictionName.toLowerCase() : '';
              if (source.sortOrder) {
                return nameA < nameB ? -1 : nameA > nameB ? 1 : 0;
              } else {
                return nameA > nameB ? -1 : nameA < nameB ? 1 : 0;
              }
            } catch (error) {
              console.error("Error during sorting:", error, a, b);
              return 0;
            }
          });
        }

        return restrictions;
      })
    );

    this.filterTextChanged.pipe(debounceTime(300)).subscribe(text => {
      this.restrictionTable.filterGlobal(text, 'contains');
    });
  }

  pageChange(event: any): void {
    this.first = event.first;
  }

  confirmDelete(restriction: Restriction) {
    this.restrictionToDelete = restriction;
    this.showDeleteConfirmDialog = true;
  }

  onSort(event: SortEvent) {
    const data = (event.data as any[]) ?? [];
    const field = event.field ?? '';
    const order = event.order ?? 1;

    const idNum = (val: unknown) => {
      const m = String(val ?? '').match(/\d+/g);
      return m ? parseInt(m[m.length - 1], 10) : Number.NEGATIVE_INFINITY;
    };

    const norm = (val: unknown) => {
      if (field === 'displayName') return idNum(val);
      if (field === 'dateCreated' || field === 'dateUpdated') {
        return val ? new Date(val as any).getTime() : 0;
      }
      if (typeof val === 'boolean') return Number(val);
      if (typeof val === 'string') return val.toLowerCase();
      return val ?? '';
    };

    data.sort((a, b) => {
      const A = norm(a[field as keyof typeof a]);
      const B = norm(b[field as keyof typeof b]);
      return (A < B ? -1 : A > B ? 1 : 0) * order;
    });
  }

  public toggleSortOrder(): void {
    this.sortOrderSubject.next(!this.sortOrderSubject.value)
  }

  cancelDelete() {
    this.restrictionToDelete = null;
    this.showDeleteConfirmDialog = false;
  }

  deleteConfirmed() {
    if (this.restrictionToDelete?.restrictionId) {
      this.restrictionsService.deleteRestriction$(this.restrictionToDelete.restrictionId).subscribe({
        next: () => {
          this.restrictionsService.refreshRestrictions();
          this.showDeleteConfirmDialog = false;
          this.restrictionToDelete = null;
        },
        error: () => {
          this.showDeleteConfirmDialog = false;
          this.restrictionToDelete = null;
        }
      });
    }
  }

  onEdit(restriction: Restriction) {
    this.selectedRestriction = { ...restriction };
    this.showDialog = true;
  }

  openDialog() {
    this.showDialog = true;
    this.selectedRestriction = null;
  }

  handleSave(data: any) {
    this.restrictionsService.refreshRestrictions();
    this.showDialog = false;
    this.selectedRestriction = null;
  }

  onGlobalFilter(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    this.filterTextChanged.next(value);
  }

  onDialogVisibleChange(v: boolean) {
    this.showDialog = v;
    if (!v) this.selectedRestriction = null; 
  }
}
